/**
 * Utilitaires pour gérer les cookies
 */

interface CookieOptions {
  expires?: Date;
  maxAge?: number; // en secondes
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
}

/**
 * Définit un cookie
 * @param name - Nom du cookie
 * @param value - Valeur du cookie
 * @param options - Options du cookie
 */
export const setCookie = (name: string, value: string, options: CookieOptions = {}): void => {
  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (options.expires) {
    cookieString += `; expires=${options.expires.toUTCString()}`;
  }

  if (options.maxAge) {
    cookieString += `; max-age=${options.maxAge}`;
  }

  if (options.path) {
    cookieString += `; path=${options.path}`;
  }

  if (options.domain) {
    cookieString += `; domain=${options.domain}`;
  }

  if (options.secure) {
    cookieString += `; secure`;
  }

  if (options.sameSite) {
    cookieString += `; samesite=${options.sameSite}`;
  }

  document.cookie = cookieString;
};

/**
 * Récupère la valeur d'un cookie
 * @param name - Nom du cookie
 * @returns La valeur du cookie ou null si non trouvé
 */
export const getCookie = (name: string): string | null => {
  const nameEQ = encodeURIComponent(name) + '=';
  const cookies = document.cookie.split(';');

  for (const cookie of cookies) {
    const c = cookie.trim();
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length));
    }
  }
  return null;
};

/**
 * Supprime un cookie
 * @param name - Nom du cookie
 * @param path - Chemin du cookie (optionnel)
 * @param domain - Domaine du cookie (optionnel)
 */
export const deleteCookie = (name: string, path?: string, domain?: string): void => {
  let cookieString = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT`;

  if (path) {
    cookieString += `; path=${path}`;
  }

  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  document.cookie = cookieString;
};

/**
 * Sauvegarde les données utilisateur et le token dans les cookies
 * @param userData - Données utilisateur
 * @param token - Token d'authentification
 */
export const saveUserDataToCookies = (userData: any, token: string): void => {
  try {
    // Sauvegarder les données utilisateur
    setCookie('ellogy_user', JSON.stringify(userData), {
      maxAge: 7 * 24 * 60 * 60, // 7 jours
      path: '/',
      secure: window.location.protocol === 'https:',
      sameSite: 'lax'
    });

    // Sauvegarder le token
    setCookie('ellogy_token', token, {
      maxAge: 7 * 24 * 60 * 60, // 7 jours
      path: '/',
      secure: window.location.protocol === 'https:',
      sameSite: 'lax'
    });

  } catch (error) {
    console.error('Erreur lors de la sauvegarde des cookies:', error);
  }
};

/**
 * Récupère les données utilisateur depuis les cookies
 * @returns Les données utilisateur ou null si non trouvées
 */
export const getUserDataFromCookies = (): any | null => {
  try {
    const userData = getCookie('ellogy_user');
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des données utilisateur:', error);
    return null;
  }
};

/**
 * Récupère le token depuis les cookies
 * @returns Le token ou null si non trouvé
 */
export const getTokenFromCookies = (): string | null => {
  return getCookie('ellogy_token');
};

/**
 * Supprime toutes les données utilisateur des cookies
 */
export const clearUserDataFromCookies = (): void => {
  deleteCookie('ellogy_user', '/');
  deleteCookie('ellogy_token', '/');
};